import { fetchWithAuth } from '../AuthService';
import { AI_SERVICE_BASE_URL } from '../AiApiService';
import { v4 as uuidv4 } from 'uuid';

/**
 * Retry utility with exponential backoff
 * @param {Function} asyncFn - Async function to retry
 * @param {number} maxRetries - Maximum number of retries (default: 4)
 * @param {number} baseDelay - Base delay in milliseconds (default: 1000)
 * @param {string} operationName - Name of operation for logging
 * @returns {Promise} Result of the async function
 */
const retryWithExponentialBackoff = async (asyncFn, maxRetries = 4, baseDelay = 1000, operationName = 'operation') => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`AiDocumentProcessingApi: Attempting ${operationName} (attempt ${attempt}/${maxRetries})`);
      
      const result = await asyncFn();
      
      // Check if the response indicates success
      if (result.ok) {
        if (attempt > 1) {
          console.log(`AiDocumentProcessingApi: ${operationName} succeeded on attempt ${attempt}`);
        }
        return result;
      }
      
      // If response is not ok, treat as an error
      const errorText = await result.text();
      throw new Error(`HTTP ${result.status}: ${errorText}`);
      
    } catch (error) {
      lastError = error;
      console.warn(`AiDocumentProcessingApi: ${operationName} failed on attempt ${attempt}:`, error.message);
      
      // If this is the last attempt, don't wait
      if (attempt === maxRetries) {
        console.error(`AiDocumentProcessingApi: ${operationName} failed after ${maxRetries} attempts:`, error);
        break;
      }
      
      // Calculate delay with exponential backoff: baseDelay * 2^(attempt-1)
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.log(`AiDocumentProcessingApi: Waiting ${delay}ms before retry ${attempt + 1}...`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // If we get here, all retries failed
  throw lastError;
};

/**
 * AI Document Processing API service - Document processing endpoints on the AI Service
 * Based on AI_API.postman_collection.json endpoints
 * All requests include retry logic with exponential backoff (4 attempts max)
 */
export const AiDocumentProcessingApi = {
  /**
   * Process document fully (metadata + chunks) using unified AI endpoint
   * Endpoint: POST /api/documents/{documentId}/process/full
   * @param {string|number} documentId - Document ID
   * @param {Object} options - Optional processing options (e.g., documentTypeId, courseName, flags)
   * @returns {Promise<Response>} Full processing response
   */
  processDocumentFull: async (documentId, options = {}) => {
    const { correlationId: externalCorrelationId, ...restOptions } = options;
    const correlationId = externalCorrelationId || uuidv4();
    const requestBody = { ...restOptions };
    console.log(`[FULL_PROCESS] Starting full processing with correlation ID: ${correlationId}`);
    return retryWithExponentialBackoff(
      () => fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/documents/${documentId}/process/full`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Correlation-ID': correlationId
        },
        body: JSON.stringify(requestBody),
      }),
      4,
      2000,
      `full processing for document ${documentId}`
    );
  },

  /**
   * Process document as syllabus using AI (UPDATED for new endpoint)
   * Endpoint: POST /api/syllabi/{document_id}/process/highend
   * @param {string|number} documentId - Document ID (syllabus document)
   * @param {Object} options - Processing options
   * @param {string} options.courseName - Course name for context
   * @returns {Promise<Response>} Enhanced syllabus processing response with course preview, events, and assignments
   */
  processDocumentAsSyllabus: async (documentId, options = {}) => {
    const { courseName, ...otherOptions } = options;
    const requestBody = {
      ...(courseName && { courseName }),
      ...otherOptions
    };
    
    // Generate correlation ID for tracking this syllabus processing operation
    const correlationId = uuidv4();
    console.log(`[SYLLABUS] Starting syllabus processing with correlation ID: ${correlationId}`);
    
    return retryWithExponentialBackoff(
      () => fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/syllabi/${documentId}/process`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Correlation-ID': correlationId
        },
        body: JSON.stringify(requestBody),
      }),
      4, // maxRetries
      2000, // baseDelay (2 seconds for more complex syllabus processing)
      `syllabus processing for document ${documentId}`
    );
  },

  /**
   * Check the current scan status for a syllabus document.
   * Lightweight GET — does not trigger processing.
   * @param {number|string} documentId
   * @returns {Promise<{document_id, status, hasSyllabusResult, summary?}>}
   */
  getSyllabusScanStatus: async (documentId) => {
    const resp = await fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/syllabi/${documentId}/status`);
    if (!resp.ok) {
      const errText = await resp.text().catch(() => '');
      throw new Error(`Status check failed (${resp.status}): ${errText}`);
    }
    return resp.json();
  },


  // Legacy methods removed: processDocumentMetadata & processDocumentForRAG (use processDocumentFull instead)
  /**
   * Process multiple documents fully with individual retry logic
   * @param {Array} documentIds - Array of document IDs
   * @param {Object} options - Optional per-document options (applied uniformly)
   * @returns {Promise<Array>} Array of processing responses (some may be errors)
   */
  processMultipleDocumentsFull: async (documentIds, options = {}) => {
    console.log(`AiDocumentProcessingApi: Starting batch FULL processing for ${documentIds.length} documents`);
    const processingPromises = documentIds.map(async (documentId, index) => {
      try {
        console.log(`AiDocumentProcessingApi: Starting FULL processing for document ${documentId} (${index + 1}/${documentIds.length})`);
        const result = await retryWithExponentialBackoff(
          () => fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/documents/${documentId}/process/full`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...options }),
          }),
          4,
          2000,
          `batch full processing for document ${documentId}`
        );
        console.log(`AiDocumentProcessingApi: Completed FULL processing for document ${documentId}`);
        return { documentId, success: true, response: result };
      } catch (error) {
        console.error(`AiDocumentProcessingApi: Failed FULL processing for document ${documentId}:`, error);
        return { documentId, success: false, error: error.message };
      }
    });
    const results = await Promise.allSettled(processingPromises);
    const processedResults = results.map(r => r.status === 'fulfilled' ? r.value : { success: false, error: r.reason?.message || 'Unknown error' });
    const successCount = processedResults.filter(r => r.success).length;
    const failureCount = processedResults.length - successCount;
    console.log(`AiDocumentProcessingApi: Batch FULL processing completed - ${successCount} succeeded, ${failureCount} failed`);
    return processedResults;
  }
};